import { AlertCircle, Check, Eye } from "@untitledui/icons"
import { motion, useReducedMotion } from "motion/react"

import { Avatar } from "@/components/base/avatar/avatar"
import { cx } from "@/utils/cx"

interface MessageUser {
  name?: string
  avatarUrl?: string
  status?: "online" | "offline"
  me?: boolean
}

interface MessageAttachment {
  type: string
  name: string
  size: string
}

interface MessageReply {
  text: string
}

interface MessageReaction {
  content: string
  count: number
}

export interface Message {
  id: string
  sentAt: string
  text?: string
  user: MessageUser
  // "narrator" renders stage-direction prose as a quiet, centered aside
  // (no bubble, no avatar) rather than a chat bubble. Defaults to a bubble.
  variant?: "bubble" | "narrator"
  reply?: MessageReply
  attachment?: MessageAttachment
  reactions?: MessageReaction[]
}

interface MessageItemProps {
  msg: Message
  className?: string
  // Whether to play the entrance animation on mount. Defaults to true. The chat
  // surface sets this false for historical backlog (resume/replay) so the
  // transcript paints statically — mounting must not imply animating. Also
  // suppressed when the user prefers reduced motion.
  animateEntrance?: boolean
  // Seconds to delay this item's entrance animation. The chat surface staggers
  // newly-arrived NPC beats so they unfold one after another. Defaults to 0
  // (immediate). Ignored when the user prefers reduced motion.
  revealDelay?: number
  // Message grouping: when consecutive beats share a speaker, repeated chrome is
  // hidden so a run reads as one cluster (à la iMessage/Slack). All default to
  // true so standalone usages render the full chrome.
  showAvatar?: boolean // NPC avatar — show on the first beat of a run
  showHeader?: boolean // NPC name + time — show on the first beat of a run
  showTimestamp?: boolean // "me" timestamp — show on the last beat of a run
  // NPC header time. Split out from showHeader so the name can show without the
  // timestamp; the chat surface currently hard-codes this false (distracting),
  // but the grouping logic still flows through so it can be re-enabled later.
  showHeaderTimestamp?: boolean
}

interface MessageStatusProps {
  status: "sent" | "read" | "failed"
  readAt?: string
  className?: string
}

export function MessageStatus({
  status,
  readAt,
  className,
}: MessageStatusProps) {
  return (
    <div
      className={cx("text-tertiary flex items-center gap-1 text-xs", className)}
    >
      {status === "sent" && (
        <>
          <Check className="size-3.5" />
          <span>Sent</span>
        </>
      )}
      {status === "read" && (
        <>
          <Eye className="size-3.5" />
          <span>Read{readAt ? ` ${readAt}` : ""}</span>
        </>
      )}
      {status === "failed" && (
        <>
          <AlertCircle className="text-error-500 size-3.5" />
          <span className="text-error-500">Failed to send</span>
        </>
      )}
    </div>
  )
}

export function MessageItem({
  msg,
  className,
  animateEntrance = true,
  revealDelay = 0,
  showAvatar = true,
  showHeader = true,
  showTimestamp = true,
  showHeaderTimestamp = true,
}: MessageItemProps) {
  const isMe = !!msg.user.me
  const isNarrator = msg.variant === "narrator"
  const name = msg.user.name || (isMe ? "You" : "User")
  const reduceMotion = useReducedMotion()

  // Staggered entrance: each beat rises and fades in. Disabled (static) when the
  // beat is historical backlog (animateEntrance=false) or the user prefers
  // reduced motion — mounting must not imply animating. motion only animates on
  // mount, so already-rendered messages don't re-animate on re-render (keys are
  // stable). The feel is variant-aware: narration drifts in slowly so it reads
  // as a held beat, NPC speech settles gently to soften the jump from the long
  // "typing" wait, and the user's own messages stay snappy.
  const revealProps =
    !animateEntrance || reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: isNarrator ? 10 : isMe ? 6 : 8 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: isNarrator ? 0.6 : isMe ? 0.3 : 0.55,
            delay: revealDelay,
            ease: "easeOut" as const,
          },
        }

  // Narrator aside: a quiet, centered stage direction between speech bubbles.
  if (isNarrator) {
    return (
      <motion.li
        className={cx("flex justify-center px-4", className)}
        data-message-id={msg.id}
        data-message-variant="narrator"
        {...revealProps}
      >
        <p className="text-secondary max-w-[80%] text-center text-sm leading-relaxed text-balance italic">
          {msg.text}
        </p>
      </motion.li>
    )
  }

  return (
    <motion.li
      className={cx("flex items-start gap-3", isMe && "justify-end", className)}
      data-message-id={msg.id}
      {...revealProps}
    >
      {/* Avatar (hidden for "me" to match iOS-style bubbles). On grouped
          continuation beats it stays mounted but invisible so the run's bubbles
          keep aligning under the speaker's avatar. */}
      {!isMe && (
        <Avatar
          className={cx(!showAvatar && "invisible")}
          src={msg.user.avatarUrl}
          alt={name}
          initials={name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()}
          status={msg.user.status}
        />
      )}

      <div
        className={cx(
          "flex max-w-[75%] flex-col space-y-1",
          isMe && "items-end",
        )}
      >
        {/* Header row (hide for "me"; on grouped continuation beats it's
            suppressed so name + time show only once per run). */}
        {!isMe && showHeader && (
          <div className="flex w-full items-baseline justify-between">
            <span className="text-sm font-semibold">{name}</span>
            {showHeaderTimestamp && (
              <span className="text-tertiary text-xs">{msg.sentAt}</span>
            )}
          </div>
        )}

        {/* Reply preview (inside the bubble for consistency) */}
        {msg.reply && !isMe && (
          <div className="border-border-secondary bg-secondary mb-1 w-fit rounded-lg border-l-2 py-1.5 pr-2 pl-3">
            <p className="text-secondary text-sm">{msg.reply.text}</p>
          </div>
        )}

        {/* Chat bubble */}
        {msg.text && (
          <div
            className={cx(
              "rounded-2xl px-4 py-2 shadow-sm",
              isMe
                ? "bg-brand-600 dark:bg-brand-500 text-white"
                : "bg-white text-gray-900 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-700",
            )}
          >
            <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
          </div>
        )}

        {/* Attachment bubble */}
        {msg.attachment && (
          <div
            className={cx(
              "mt-2 flex items-center gap-2 rounded-2xl px-4 py-2 shadow-sm ring-1",
              isMe
                ? "bg-brand-600/95 ring-brand-500/40 text-white"
                : "bg-white text-gray-900 ring-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-700",
            )}
          >
            <div className="bg-secondary flex size-10 items-center justify-center rounded-lg">
              <span className="text-secondary text-xs font-semibold uppercase">
                {msg.attachment.type}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-primary truncate text-sm font-medium">
                {msg.attachment.name}
              </p>
              <p className="text-tertiary text-xs">{msg.attachment.size}</p>
            </div>
          </div>
        )}

        {/* Timestamp for "me" below bubble (subtle); on grouped runs it shows
            only on the last beat so a burst of messages reads as one. */}
        {isMe && showTimestamp && (
          <span className="text-tertiary text-[10px]">{msg.sentAt}</span>
        )}
      </div>
    </motion.li>
  )
}
